import { useMemo, useState, useCallback, type PropsWithChildren } from "react";
import { useRouterState } from "@tanstack/react-router";
import { AppHeader } from "@/app/layouts/AppShellHeader";
import { AppNavigationRail } from "@/app/layouts/AppNavigationRail";
import { useAuth } from "@/app/providers/useAuth";

export function AppShell({ children }: PropsWithChildren) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { roles } = useAuth();

  const isAdminMode = useRouterState({
    select: (state) => state.location.pathname.startsWith("/admin"),
  });

  const isAdminAllowed = useMemo(() => roles.includes("Admin"), [roles]);

  const canViewCategories = useMemo(
    () =>
      roles.some(
        (role) => role === "Admin" || role === "User" || role === "DataProvider"
      ),
    [roles]
  );

  const handleToggleSidebar = useCallback(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      setIsSidebarOpen((previous) => !previous);
    } else {
      setIsSidebarCollapsed((previous) => !previous);
    }
  }, []);

  const closeMobileSidebar = useCallback(() => setIsSidebarOpen(false), []);

  return (
    <div className="relative flex h-screen bg-carbon-950 text-ink-900 antialiased">
      {isSidebarOpen ? (
        <div
          className="fixed inset-0 z-20 bg-carbon-900/60 backdrop-blur-sm md:hidden"
          aria-hidden="true"
          onClick={closeMobileSidebar}
        />
      ) : null}
      <AppNavigationRail
        collapsed={isSidebarCollapsed}
        mobileOpen={isSidebarOpen}
        isAdminMode={isAdminMode && isAdminAllowed}
        isAdminAllowed={isAdminAllowed}
        canViewCategories={canViewCategories}
        onToggleCollapse={() => setIsSidebarCollapsed((previous) => !previous)}
        onNavigate={closeMobileSidebar}
        onCloseMobile={closeMobileSidebar}
      />
      <div className="relative flex h-full flex-1 flex-col min-w-0 min-h-0 overflow-hidden">
        <AppHeader
          isSidebarCollapsed={isSidebarCollapsed}
          onToggleSidebar={handleToggleSidebar}
          isAdminMode={isAdminMode && isAdminAllowed}
          isAdminAllowed={isAdminAllowed}
        />
        <main className="relative flex flex-1 flex-col min-w-0 min-h-0 overflow-hidden bg-white">
          <div className="absolute inset-x-0 top-0 h-24 w-full bg-linear-to-b from-imperial-600/35 via-imperial-500/15 to-transparent blur-3xl" />
          <div className="relative z-10 h-full flex flex-col gap-8 border border-white/10 bg-white/95 shadow-chrome md:px-2 min-w-0 min-h-0 overflow-hidden">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
